import {
  isIndependentResponse,
  type MultiplicationAttempt,
} from "./attempt.ts";
import {
  buildFluencyBaseline,
  classifyAttemptFluency,
  type FluencyBaseline,
} from "./fluency.ts";
import type { MultiplicationFactId } from "./model.ts";

export const TABLE_MASTERY_STAGES = [
  "UNSEEN",
  "LEARNING",
  "PRACTISING",
  "FLUENT",
  "MASTERED",
] as const;
export type TableMasteryStage = (typeof TABLE_MASTERY_STAGES)[number];
export type TableRetentionState = "NOT_DUE" | "STABLE" | "DUE" | "REFRESH_DUE";

export interface TableMasteryRules {
  readonly version: string;
  readonly practisingSuccesses: number;
  readonly fluentWindowSize: number;
  readonly fluentSuccesses: number;
  readonly fluentSessions: number;
  readonly masteredFluentSuccesses: number;
  readonly masteredSessions: number;
  readonly retentionGapMs: number;
  readonly maintenanceDueMs: number;
}

export const DEFAULT_TABLE_MASTERY_RULES: TableMasteryRules = {
  version: "table-mastery-v1",
  practisingSuccesses: 3,
  fluentWindowSize: 5,
  fluentSuccesses: 4,
  fluentSessions: 2,
  masteredFluentSuccesses: 5,
  masteredSessions: 3,
  retentionGapMs: 24 * 60 * 60 * 1000,
  maintenanceDueMs: 7 * 24 * 60 * 60 * 1000,
};

export interface TableFactMastery {
  readonly factId: MultiplicationFactId;
  readonly currentStage: TableMasteryStage;
  readonly highestStageEver: TableMasteryStage;
  readonly retentionState: TableRetentionState;
  readonly attemptCount: number;
  readonly independentResponseCount: number;
  readonly independentSuccessCount: number;
  readonly fluentSuccessCount: number;
  readonly sessionCount: number;
  readonly lastSeenAt: string | null;
  readonly lastSuccessAt: string | null;
  readonly rulesetVersion: string;
}

function rank(stage: TableMasteryStage): number {
  return TABLE_MASTERY_STAGES.indexOf(stage);
}

function maxStage(a: TableMasteryStage, b: TableMasteryStage): TableMasteryStage {
  return rank(a) >= rank(b) ? a : b;
}

function distinctSessions(attempts: readonly MultiplicationAttempt[]): number {
  return new Set(attempts.map((attempt) => attempt.sessionId)).size;
}

function hasRetentionGap(
  attempts: readonly MultiplicationAttempt[],
  requiredGapMs: number,
): boolean {
  for (let index = 1; index < attempts.length; index += 1) {
    const previous = Date.parse(attempts[index - 1].submittedAt);
    const current = Date.parse(attempts[index].submittedAt);
    if (current - previous >= requiredGapMs) return true;
  }
  return false;
}

function rawStage(
  attempts: readonly MultiplicationAttempt[],
  baseline: FluencyBaseline,
  rules: TableMasteryRules,
): TableMasteryStage {
  if (attempts.length === 0) return "UNSEEN";
  const independent = attempts.filter(isIndependentResponse);
  if (independent.length === 0) return "LEARNING";

  let stage: TableMasteryStage = "LEARNING";
  const successes = independent.filter((attempt) => attempt.correct === true);
  const latestTwo = independent.slice(-2);
  if (
    successes.length >= rules.practisingSuccesses
    && latestTwo.length === 2
    && latestTwo.every((attempt) => attempt.correct === true)
  ) {
    stage = "PRACTISING";
  }

  const retrievalWindow = independent.slice(-rules.fluentWindowSize);
  const fluentWindow = retrievalWindow.filter(
    (attempt) => classifyAttemptFluency(attempt, baseline) === "FLUENT",
  );
  if (
    retrievalWindow.length >= rules.fluentWindowSize
    && fluentWindow.length >= rules.fluentSuccesses
    && distinctSessions(fluentWindow) >= rules.fluentSessions
  ) {
    stage = "FLUENT";
  }

  const fluentSuccesses = independent.filter(
    (attempt) => classifyAttemptFluency(attempt, baseline) === "FLUENT",
  );
  const masteredTail = independent.slice(-4);
  if (
    fluentSuccesses.length >= rules.masteredFluentSuccesses
    && distinctSessions(fluentSuccesses) >= rules.masteredSessions
    && masteredTail.length === 4
    && masteredTail.every((attempt) => classifyAttemptFluency(attempt, baseline) === "FLUENT")
    && hasRetentionGap(fluentSuccesses, rules.retentionGapMs)
  ) {
    stage = "MASTERED";
  }

  return stage;
}

export function evaluateTableFactMastery(
  factId: MultiplicationFactId,
  allAttempts: readonly MultiplicationAttempt[],
  now: string,
  baseline: FluencyBaseline = buildFluencyBaseline(allAttempts),
  rules: TableMasteryRules = DEFAULT_TABLE_MASTERY_RULES,
): TableFactMastery {
  const attempts = allAttempts
    .filter((attempt) => attempt.factId === factId)
    .sort((a, b) => Date.parse(a.submittedAt) - Date.parse(b.submittedAt));

  let highestStageEver: TableMasteryStage = "UNSEEN";
  for (let count = 1; count <= attempts.length; count += 1) {
    highestStageEver = maxStage(
      highestStageEver,
      rawStage(attempts.slice(0, count), baseline, rules),
    );
  }

  let currentStage = rawStage(attempts, baseline, rules);
  const independent = attempts.filter(isIndependentResponse);
  const latestIndependent = independent.at(-1) ?? null;
  const lastSuccess = [...independent].reverse().find((attempt) => attempt.correct === true) ?? null;
  let retentionState: TableRetentionState = "NOT_DUE";

  if (rank(highestStageEver) >= rank("FLUENT") && latestIndependent?.correct === false) {
    retentionState = "REFRESH_DUE";
    currentStage = maxStage("PRACTISING", currentStage);
  } else if (highestStageEver === "MASTERED" && lastSuccess) {
    retentionState = Date.parse(now) - Date.parse(lastSuccess.submittedAt) >= rules.maintenanceDueMs
      ? "DUE"
      : "STABLE";
  }

  const fluentSuccesses = independent.filter(
    (attempt) => classifyAttemptFluency(attempt, baseline) === "FLUENT",
  );

  return {
    factId,
    currentStage,
    highestStageEver,
    retentionState,
    attemptCount: attempts.length,
    independentResponseCount: independent.length,
    independentSuccessCount: independent.filter((attempt) => attempt.correct === true).length,
    fluentSuccessCount: fluentSuccesses.length,
    sessionCount: distinctSessions(independent),
    lastSeenAt: attempts.at(-1)?.submittedAt ?? null,
    lastSuccessAt: lastSuccess?.submittedAt ?? null,
    rulesetVersion: rules.version,
  };
}
